import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Header, track, useT } from './Chrome';
import { SiteFooterV2 } from './Footers';
import luckyReading from './luckyReading';
import auspiciousNumerology from '../public/lib/auspiciousNumerology';

// Phone Number Astrology — /lucky-number. The reading is computed entirely in
// the browser (luckyReading.js): the number is never sent to the server, which
// the page says out loud. Facts come from the auspiciousNumerology lib.

function Stars({ n }) {
  return (
    <span className="lucky-stars" aria-label={`${n} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= n ? 'on' : ''}>★</span>
      ))}
    </span>
  );
}

// Share-card URLs: language follows the page locale; v=<epoch week> makes
// social/CDN caches roll over when the weekly card theme rotates server-side.
const EPOCH_WEEK = Math.floor(Date.now() / (7 * 24 * 3600 * 1000));
function cardLang() {
  const lang = (window.__MT && window.__MT.locale && window.__MT.locale.lang) || 'en';
  return ['en', 'th', 'cn', 'jp', 'ko', 'es', 'ru'].includes(lang) ? lang : 'en';
}
function cardUrl(digits, format) {
  return `/lucky-number/og/${digits}.png?l=${cardLang()}&f=${format}&v=${EPOCH_WEEK}`;
}

export default function LuckyPage({ me, currency, onCurrency }) {
  const t = useT();
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [hint, setHint] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  const reading = useMemo(() => (submitted ? luckyReading(submitted) : null), [submitted]);
  const facts = useMemo(() => (submitted ? auspiciousNumerology(submitted) : []), [submitted]);
  const digits = submitted.replace(/\D/g, '');

  // Track page view once
  useEffect(() => { track('lucky_page_view'); }, []);

  // Shared link (?n=...) — run the reading immediately.
  useEffect(() => {
    const n = new URLSearchParams(window.location.search).get('n');
    const d = (n || '').replace(/\D/g, '');
    if (d.length >= 4) {
      setPhone(d);
      setSubmitted(d);
      track('lucky_shared_link_open', { digits: d.length });
    }
  }, []);

  // Keep the URL shareable: /lucky-number?n=<digits> re-runs this reading.
  useEffect(() => {
    if (!reading) return;
    window.history.replaceState(null, '', `/lucky-number?n=${digits}`);
  }, [reading, digits]);

  // Track reading result (no phone number — privacy)
  useEffect(() => {
    if (!reading) return;
    track('lucky_reading_result', {
      score: reading.score,
      tier: reading.tier,
      path: reading.path,
      stars: reading.stars,
      verse: reading.verse ? reading.verse.reference : null,
    });
  }, [reading]);

  function submit(e) {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) {
      setHint(true);
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    setHint(false);
    setSubmitted(phone);
    track('lucky_reading', { digits: digits.length });
    window.requestAnimationFrame(() => {
      const el = document.querySelector('.lucky-result');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />

      <section className="hero" style={{ backgroundImage: 'linear-gradient(rgba(48,164,174,.97), rgba(30,147,157,1))' }}>
        <h1 className="hero-title">{t('lucky_title')} <b>{t('lucky_title_bold')}</b></h1>
        <p className="hero-sub">{t('lucky_subtitle')}</p>
        <form className="hero-topup" onSubmit={submit}>
          <div className="phone-input big">
            <span className="cc" aria-hidden="true">☾☀</span>
            <input
              ref={inputRef}
              className="num"
              type="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setHint(false); }}
              placeholder={t('lucky_placeholder')}
              aria-label="Phone number to read"
            />
          </div>
          <button type="submit" className="btn hero-btn">{t('lucky_button')}</button>
        </form>
        {hint && <p className="hero-sub">{t('lucky_hint')}</p>}
        <p className="hero-sub lucky-privacy">{t('lucky_privacy')}</p>
      </section>

      <div className="body">
        <main className="main" style={{ flex: '1 1 auto' }}>
          {!reading && (
            <div className="cocard">
              <h2>{t('lucky_how_title')}</h2>
              <p dangerouslySetInnerHTML={{ __html: t('lucky_how_p1') }} />
              <p dangerouslySetInnerHTML={{ __html: t('lucky_how_p2') }} />
              <p dangerouslySetInnerHTML={{ __html: t('lucky_how_p3') }} />
            </div>
          )}

          {reading && (
            <div className="lucky-result">
              <div className="cocard lucky-scorecard">
                <div className="lucky-score">
                  <div className="lucky-score-num">{reading.score}<span>/100</span></div>
                  <div>
                    <Stars n={reading.stars} />
                    <div className="lucky-tier">{reading.tier}</div>
                  </div>
                </div>
                <div className="lucky-meter" role="img" aria-label={`Auspiciousness ${reading.score} out of 100`}>
                  <div className="lucky-meter-fill" style={{ width: `${reading.score}%` }} />
                </div>
                <p className="lucky-path">
                  <b>{t('lucky_path_number')} {reading.path}</b> ({t('lucky_digits_sum')} {reading.sum}). {reading.pathReading}
                </p>
                <div className="chips lucky-chips">
                  <span className="chip">{t('lucky_ruled_by')} {reading.ruling.planet}</span>
                  <span className="chip">{t('lucky_lucky_day')} {reading.ruling.day}</span>
                  <span className="chip">{t('lucky_lucky_color')} {reading.ruling.color}</span>
                </div>
                <p className="lucky-tail">{reading.tail.note}</p>
              </div>

              <div className="cocard">
                <h2>{t('lucky_pair_title')}</h2>
                <p className="lucky-note" dangerouslySetInnerHTML={{ __html: t('lucky_pair_note') }} />
                <table className="lucky-pairs">
                  <tbody>
                    {reading.pairs.map((p, i) => (
                      <tr key={`${p.pair}-${i}`} className={p.emphasized ? 'emph' : ''}>
                        <td className="lucky-pair-num">{p.pair}</td>
                        <td className="lucky-pair-score">
                          <span className={`tag ${p.score >= 7 ? 'good' : p.score <= 3 ? 'bad' : ''}`}>{p.score}/10</span>
                        </td>
                        <td>{p.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="lucky-note">
                  {t('lucky_best_pair')} <b>{reading.best.pair}</b> — {reading.best.meaning}
                  {reading.worst.score < 5 && (
                    <> · {t('lucky_watch_out')} <b>{reading.worst.pair}</b> — {reading.worst.meaning}</>
                  )}
                </p>
              </div>

              <div className="cocard">
                <h2>{t('lucky_facts_title')}</h2>
                <table className="lucky-pairs lucky-facts">
                  <tbody>
                    {facts.map((f) => (
                      <tr key={f.label}>
                        <td className="lucky-pair-num">{f.label}</td>
                        <td>{f.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {reading.verse && (
                <div className="cocard lucky-verse">
                  <h2>{t('lucky_verse_title')}</h2>
                  <blockquote>"{reading.verse.text}"</blockquote>
                  <cite>— {reading.verse.reference}</cite>
                </div>
              )}

              <div className="cocard lucky-share">
                <h2>{t('lucky_share_title')}</h2>
                <p className="lucky-note">{t('lucky_share_note')}</p>
                <div className="lucky-share-cards">
                  <a href={cardUrl(digits, 'wide')} download={`lucky-${digits}.png`} onClick={() => track('lucky_card_download', { format: 'wide' })}>
                    <img className="wide" src={cardUrl(digits, 'wide')} alt={`Lucky number card for ${digits}`} loading="lazy" />
                  </a>
                  <a href={cardUrl(digits, 'square')} download={`lucky-${digits}-square.png`} onClick={() => track('lucky_card_download', { format: 'square' })}>
                    <img className="square" src={cardUrl(digits, 'square')} alt={`Lucky number card for ${digits} (square)`} loading="lazy" />
                  </a>
                </div>
                <div className="lucky-share-btns">
                  {(() => {
                    const shareUrl = `https://mobiletopup.com/lucky-number?n=${digits}`;
                    const shareText = t('lucky_share_text').replace('{score}', reading.score).replace('{tier}', reading.tier);
                    const enc = encodeURIComponent;
                    const links = [
                      ['WhatsApp', `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`],
                      ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`],
                      ['X', `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareText)}`],
                      ['LINE', `https://social-plugins.line.me/lineit/share?url=${enc(shareUrl)}`],
                      ['Telegram', `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`],
                    ];
                    return (
                      <>
                        {links.map(([name, href]) => (
                          <a key={name} className="btn lucky-share-btn" href={href} target="_blank" rel="noopener noreferrer"
                            onClick={() => track('lucky_share', { channel: name.toLowerCase() })}>
                            {name}
                          </a>
                        ))}
                        {navigator.share && (
                          <button type="button" className="btn lucky-share-btn" onClick={() => {
                            track('lucky_share', { channel: 'native' });
                            navigator.share({ title: 'Lucky Number Reading', text: shareText, url: shareUrl }).catch(() => {});
                          }}>{t('lucky_share_native')}</button>
                        )}
                        <button type="button" className="btn lucky-share-btn" onClick={() => {
                          track('lucky_share', { channel: 'copy' });
                          navigator.clipboard.writeText(shareUrl).then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }).catch(() => {});
                        }}>{copied ? t('lucky_copied') : t('lucky_copy_link')}</button>
                      </>
                    );
                  })()}
                </div>
              </div>

              <p className="lucky-disclaimer">{t('lucky_disclaimer')}</p>
            </div>
          )}
        </main>

        <aside className="rail">
          <div className="railcard trust">
            <h4>{t('lucky_sidebar_love_title')}</h4>
            <span dangerouslySetInnerHTML={{ __html: t('lucky_sidebar_love_text') }} />
          </div>
          <div className="railcard">
            <h4>{t('lucky_sidebar_travel_title')}</h4>
            <span dangerouslySetInnerHTML={{ __html: t('lucky_sidebar_travel_text') }} />
          </div>
          <div className="railcard trust">
            <h4>{t('lucky_sidebar_private_title')}</h4>
            <span dangerouslySetInnerHTML={{ __html: t('lucky_sidebar_private_text') }} />
          </div>
        </aside>
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}
